import { useCallback, useState } from 'react';
import Box from 'fe-design-base/atoms/Box';
import Icon, { IconName } from 'fe-design-base/atoms/Icon';
import Text from 'fe-design-base/atoms/Text';
import Button from 'fe-design-base/molecules/Button';
import IconButton from 'fe-design-base/molecules/IconButton';
import Pill from 'fe-design-base/molecules/Pill';
import { PillVariant } from 'fe-design-base/molecules/Pill/Pill';

import {
  approveTradeCoverRequest,
  rejectTradeCoverRequest,
} from 'features/notifications/api';
import { useRequestAction } from 'features/notifications/hooks/useRequestAction';

import { convertToTimezone, prettyDayAtTimeLowercase } from 'util/dateTime';
import { toI18n } from 'util/i18n';

type Status = 'denied_by_manager' | 'approved';

type Reason = {
  icon: IconName;
  message: string;
};

export type CoverClaim = {
  uuid: string;
  accepter: {
    first_name: string;
    last_name: string;
  };
  reasons: Reason[];
  state: string;
  estimated_pay: number;
  accepted_at_secs: number;
};

type CoverShiftCardProps = {
  claim: CoverClaim;
  isTeamRequest?: boolean;
  location: any;
};

const statuses: Record<Status, { label: string; variant: PillVariant }> = {
  denied_by_manager: {
    label: toI18n('requests_feed.statuses.denied_by_manager'),
    variant: 'error',
  },
  approved: {
    label: toI18n('requests_feed.statuses.approved'),
    variant: 'success',
  },
};

const CoverShiftCard = ({
  claim,
  isTeamRequest,
  location,
}: CoverShiftCardProps) => {
  const { isLoading: isApproving, handleAction: handleApprove } =
    useRequestAction({
      apiFunction: approveTradeCoverRequest,
      itemId: claim.uuid,
      successMessage: 'requests_feed.cover.details.approved_success',
      initiatedFrom: 'cover_shift_card',
      buttonText: toI18n('requests_feed.actions.approve'),
    });
  const { isLoading: isDenying, handleAction: handleDeny } = useRequestAction({
    apiFunction: rejectTradeCoverRequest,
    itemId: claim.uuid,
    successMessage: 'requests_feed.cover.details.denied_success',
    initiatedFrom: 'cover_shift_card',
    buttonText: toI18n('requests_feed.actions.deny'),
  });
  const [showInsights, setShowInsights] = useState(false);
  const { reasons, estimated_pay, accepted_at_secs, state, accepter } = claim;
  const { first_name, last_name } = accepter;
  const name = `${first_name} ${last_name}`;
  const handleShowInsights = useCallback(() => {
    setShowInsights(!showInsights);
  }, [showInsights]);

  // Convert accepted_at_secs to location timezone
  const timezone = location?.tz_name;
  const acceptedAtInTz = convertToTimezone(accepted_at_secs, timezone);

  return (
    <Box p={16} bw={1} bcolor="mono300" bradiusm>
      {(state === 'denied_by_manager' || state === 'approved') && (
        <Pill variant={statuses[state as Status].variant}>
          {statuses[state as Status].label}
        </Pill>
      )}
      <Text variant="bodyBoldTight">{name}</Text>
      <Box row gap={4} alignItemsCenter>
        <Text
          variant="bodySmBold"
          color="mono700"
          i18n="requests_feed.cover.details.accepted"
        />
        <Text variant="bodySm" color="mono700">
          {prettyDayAtTimeLowercase(acceptedAtInTz)}
        </Text>
      </Box>
      {isTeamRequest && (
        <>
          <Box row gap={4} alignItemsCenter>
            <Text
              variant="bodySmBold"
              color="mono700"
              i18n="requests_feed.cover.details.estimated_pay"
            />
            <Text variant="bodySm" color="mono700">
              ${estimated_pay}
            </Text>
          </Box>
        </>
      )}

      {state !== 'denied_by_manager' && (
        <div>
          {reasons.length > 0 && (
            <>
              <Box row gap={8} alignItemsCenter>
                <Text
                  variant="bodySmBold"
                  color="mono700"
                  i18n="requests_feed.cover.details.insights"
                />
                <IconButton
                  icon={showInsights ? 'ChevronUp' : 'ChevronDown'}
                  label={showInsights ? 'Hide insights' : 'Show insights'}
                  onClick={handleShowInsights}
                />
              </Box>
              {showInsights &&
                reasons.map(reason => (
                  <Box key={reason.message}>
                    <Box row gap={4} alignItemsCenter>
                      <Icon iconName={reason.icon} size="small" />
                      <Text variant="bodySm" color="mono700">
                        {reason.message}
                      </Text>
                    </Box>
                  </Box>
                ))}
            </>
          )}

          {isTeamRequest && (
            <Box row gap={8} mt={16}>
              <Button
                variant="secondary"
                fullWidth
                onClick={handleDeny}
                disabled={isDenying || isApproving}
                isLoading={isDenying}
              >
                {toI18n('requests_feed.actions.deny')}
              </Button>
              <Button
                fullWidth
                onClick={handleApprove}
                disabled={isApproving || isDenying}
                isLoading={isApproving}
              >
                {toI18n('requests_feed.actions.approve')}
              </Button>
            </Box>
          )}
        </div>
      )}
    </Box>
  );
};

export default CoverShiftCard;
